import React, { useEffect, useState } from "react";
import { http, inr, requestErrorMessage } from "@/lib/erp";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BadgeCheck, RefreshCw, Check, X, IndianRupee } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  submitted: { label: "Pending Review", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  approved: { label: "Approved", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected: { label: "Rejected", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  paid: { label: "Paid", cls: "bg-brand-50 text-brand-700 border-brand-200" },
};

export default function Approvals() {
  const [rows, setRows] = useState([]);
  const [view, setView] = useState("submitted");
  const [loadError, setLoadError] = useState("");

  const load = async () => {
    try { const r = await http.get("/expenses"); setRows(r.data); setLoadError(""); }
    catch (e) { setLoadError(requestErrorMessage(e, "load expense approvals")); }
  };
  useEffect(() => { load(); }, []);

  const act = async (id, path, body) => {
    try { await http.post(`/expenses/${id}/${path}`, body || {}); toast.success("Updated"); load(); }
    catch (e) { toast.error(requestErrorMessage(e, "update expense approval")); }
  };

  const filtered = rows.filter((r) => (r.status || "draft") === view || (view === "submitted" && (r.status || "draft") === "submitted"));

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><div className="text-xs font-semibold uppercase tracking-[.18em] text-brand-600">Approvals Desk</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mt-1">Expense Approvals</h1>
          <p className="text-sm text-muted-foreground">Submit → Approve → Paid workflow for expense claims</p></div>
        <Button variant="outline" size="icon" onClick={load} data-testid="refresh-approvals"><RefreshCw className="h-4 w-4" /></Button>
      </div>

      <div className="color-tabs inline-flex max-w-full flex-wrap rounded-lg border p-1 gap-1" role="group" aria-label="Filter approvals by status">
        {["submitted", "approved", "paid", "rejected"].map((t) => (
          <button key={t} type="button" onClick={() => setView(t)} data-testid={`approval-tab-${t}`}
            aria-pressed={view === t}
            data-tone={`approval-${t}`}
            className="color-tab px-4 py-2 rounded-md text-sm font-medium capitalize">
            {STATUS[t]?.label || t} <span className="ml-1 text-xs">{rows.filter((r) => (r.status || "draft") === t).length}</span>
          </button>
        ))}
      </div>

      {loadError && <div role="alert" className="flex items-center justify-between gap-3 border border-rose-200 bg-rose-50 rounded p-3 text-sm text-rose-700"><span>{loadError}</span><Button size="sm" variant="outline" onClick={load}>Retry</Button></div>}
      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead className="bg-slate-50"><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground"><th className="px-4 py-3 font-medium">No</th><th className="px-4 py-3 font-medium">Date</th><th className="px-4 py-3 font-medium">Payee</th><th className="px-4 py-3 font-medium">Description</th><th className="px-4 py-3 font-medium">Status</th><th className="px-4 py-3 font-medium text-right">Total</th><th className="px-4 py-3 font-medium text-right">Actions</th></tr></thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className="border-t hover:bg-slate-50" data-testid={`approval-row-${r.id}`}>
                <td className="px-4 py-3 font-mono font-medium">{r.expense_no}</td>
                <td className="px-4 py-3 text-muted-foreground">{r.expense_date}</td>
                <td className="px-4 py-3">{r.payee_name || (r.payee_type === "employee" ? "Employee" : "—")}</td>
                <td className="px-4 py-3 max-w-[240px] truncate">{r.description}</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded border ${STATUS[r.status]?.cls || "bg-slate-50 text-slate-600 border-slate-200"}`}>{STATUS[r.status]?.label || r.status || "draft"}</span></td>
                <td className="px-4 py-3 text-right font-mono font-semibold">{inr(r.total || r.amount)}</td>
                <td className="px-4 py-3"><div className="flex justify-end gap-1">
                  {r.status === "submitted" && <>
                    <button onClick={() => act(r.id, "approve")} className="p-1.5 hover:bg-emerald-50 rounded" title="Approve" data-testid={`approve-${r.id}`}><Check className="h-4 w-4 text-emerald-600" /></button>
                    <button onClick={() => act(r.id, "approve", { decision: "rejected", note: "Rejected" })} className="p-1.5 hover:bg-rose-50 rounded" title="Reject" data-testid={`reject-${r.id}`}><X className="h-4 w-4 text-rose-500" /></button>
                  </>}
                  {r.status === "approved" && <button onClick={() => act(r.id, "mark-paid")} className="px-2 py-1 text-xs rounded bg-brand-600 text-white flex items-center gap-1" data-testid={`pay-${r.id}`}><IndianRupee className="h-3 w-3" /> Mark Paid</button>}
                </div></td>
              </tr>
            ))}
            {!loadError && filtered.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-muted-foreground"><BadgeCheck className="h-8 w-8 mx-auto mb-2 opacity-40" />No {STATUS[view]?.label?.toLowerCase() || view} claims</td></tr>}
          </tbody>
        </table></div>
      </Card>
    </div>
  );
}
